import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Empty,
  Modal,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { useAuth } from "shared/contexts/AuthProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { servicesUrlConfig } from "shared/config/setting";
import { mcapLogsService } from "features/robot-logs/services/mcapLogsService";
import logger from "shared/utils/logging";

import { organizationsService } from "../services/organizationsService";
import { BUTTON_STYLES, PAGINATION_DEFAULTS } from "../constants";
import {
  createOrgMembersColumns,
  createOrgRobotColumns,
} from "../components/tableColumns";
import { AddMemberModal, EditMemberModal } from "../components/MemberModals";
import { EditOrganizationModal } from "../components/OrganizationModals";
import { OrgStorageSection, getErrorDetail } from "../components/OrgStorageSection";
import { OrgInfoHeader } from "../components/OrgInfoHeader";
import { OrgKpiRow } from "../components/OrgKpiRow";
import { RobotDetailModal } from "../components/RobotDetailModal";
import { AttachRobotModal } from "../components/AttachRobotModal";

const EMPTY_PAGE = { page: 1, per_page: PAGINATION_DEFAULTS.perPage, total: 0, results: [] };

// Non-admins only need the totals for the KPI row, so keep their payload small
// (the robots payload carries robot api_keys).
const COUNT_ONLY_PER_PAGE = 1;

/**
 * Organization page — everything about the organization currently selected in
 * the sidebar. Members, robots and storage management are only rendered for
 * organization admins and superusers, mirroring the backend's own rules
 * (raasdata-service requires org admin even to list storages).
 */
const OrganizationPage = () => {
  const { t, ready } = useTranslation();
  const translate = (key) => (ready ? t(key) : key);

  const { authFetch, isSuperUser, user } = useAuth();
  const {
    selectedOrg,
    hasRoleInSelectedOrg,
    loading: orgsLoading,
    refreshUserOrgs,
  } = useUserOrg();

  const orgId = selectedOrg?.organization?.org_id ?? null;
  const canManage = hasRoleInSelectedOrg("admin") || isSuperUser;

  const [activeTab, setActiveTab] = useState("members");

  const [members, setMembers] = useState(EMPTY_PAGE);
  const [membersLoading, setMembersLoading] = useState(false);
  const [robots, setRobots] = useState(EMPTY_PAGE);
  const [robotsLoading, setRobotsLoading] = useState(false);
  const [mcapStats, setMcapStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [isAddMemberVisible, setIsAddMemberVisible] = useState(false);
  const [newMember, setNewMember] = useState({ username: "", roles: [] });
  const [isEditMemberVisible, setIsEditMemberVisible] = useState(false);
  const [editingMember, setEditingMember] = useState({ username: "", roles: [] });

  const [isAttachRobotVisible, setIsAttachRobotVisible] = useState(false);
  const [attachingRobot, setAttachingRobot] = useState(false);
  const [robotDetail, setRobotDetail] = useState(null);

  const [isEditOrgVisible, setIsEditOrgVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState({
    org_id: null,
    name: "",
    description: "",
    logo_path: null,
    logo_compact_path: null,
  });

  const notifyFailure = (error, fallbackKey) => {
    logger.error(fallbackKey, error);
    message.error(`${translate(fallbackKey)}: ${getErrorDetail(error)}`);
  };

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadMembers = useCallback(
    async (page = PAGINATION_DEFAULTS.page, perPage) => {
      if (!orgId) return;
      const size = perPage ?? (canManage ? PAGINATION_DEFAULTS.perPage : COUNT_ONLY_PER_PAGE);
      setMembersLoading(true);
      try {
        const response = await organizationsService.getOrgMembers(
          orgId,
          authFetch,
          page,
          size,
        );
        setMembers(response.data ?? EMPTY_PAGE);
      } catch (error) {
        notifyFailure(error, "organizations.orgPage.loadError");
      } finally {
        setMembersLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, authFetch, canManage],
  );

  const loadRobots = useCallback(
    async (page = PAGINATION_DEFAULTS.page, perPage) => {
      if (!orgId) return;
      const size = perPage ?? (canManage ? PAGINATION_DEFAULTS.perPage : COUNT_ONLY_PER_PAGE);
      setRobotsLoading(true);
      try {
        const response = await organizationsService.getOrgRobots(
          orgId,
          authFetch,
          page,
          size,
        );
        setRobots(response.data ?? EMPTY_PAGE);
      } catch (error) {
        notifyFailure(error, "organizations.orgPage.loadError");
      } finally {
        setRobotsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, authFetch, canManage],
  );

  const loadMcapStats = useCallback(async () => {
    if (!orgId) return;
    setStatsLoading(true);
    try {
      const response = await mcapLogsService.getStats(authFetch, { org_id: orgId });
      setMcapStats(response.data ?? null);
    } catch (error) {
      // Log stats are supplementary — never block the rest of the page.
      logger.warn("Failed to load MCAP stats:", error);
      setMcapStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [orgId, authFetch]);

  // Reload everything when the sidebar organization changes.
  useEffect(() => {
    if (!orgId) {
      setMembers(EMPTY_PAGE);
      setRobots(EMPTY_PAGE);
      setMcapStats(null);
      return;
    }
    setActiveTab("members");
    loadMembers(PAGINATION_DEFAULTS.page);
    loadRobots(PAGINATION_DEFAULTS.page);
    loadMcapStats();
  }, [orgId, loadMembers, loadRobots, loadMcapStats]);

  // ── Member actions ────────────────────────────────────────────────────────

  const isSelf = (username) => !!username && username === user?.username;

  const handleAddMember = async () => {
    if (!newMember.username || newMember.roles.length === 0) {
      message.error(
        `${translate("organizations.pleaseEnterUsername")} ${translate("organizations.pleaseSelectRoles")}`,
      );
      return;
    }
    try {
      await organizationsService.addMember(
        orgId,
        newMember.username,
        newMember.roles,
        authFetch,
      );
      message.success(translate("organizations.memberAddedSuccess"));
      setIsAddMemberVisible(false);
      setNewMember({ username: "", roles: [] });
      loadMembers(members.page);
      if (isSelf(newMember.username)) await refreshUserOrgs();
    } catch (error) {
      notifyFailure(error, "organizations.memberAddFailed");
    }
  };

  const handleEditMember = (record) => {
    setEditingMember({
      username: record?.user?.username,
      roles: record?.roles ?? [],
    });
    setIsEditMemberVisible(true);
  };

  const handleUpdateMemberRoles = async () => {
    if (!editingMember.username) {
      message.error(translate("organizations.pleaseSelectMember"));
      return;
    }
    if (editingMember.roles.length === 0) {
      message.error(translate("organizations.pleaseSelectRoles"));
      return;
    }
    try {
      await organizationsService.updateMemberRoles(
        orgId,
        editingMember.username,
        editingMember.roles,
        authFetch,
      );
      message.success(translate("organizations.memberUpdatedSuccess"));
      setIsEditMemberVisible(false);
      loadMembers(members.page);
      // Changing your own roles can add or remove management access.
      if (isSelf(editingMember.username)) await refreshUserOrgs();
      setEditingMember({ username: "", roles: [] });
    } catch (error) {
      notifyFailure(error, "organizations.memberUpdateFailed");
    }
  };

  const handleRemoveMember = (username) => {
    Modal.confirm({
      title: translate("organizations.confirmRemoveMember"),
      content: `${translate("organizations.username")}: ${username}`,
      okText: translate("common.yes"),
      cancelText: translate("common.no"),
      onOk: async () => {
        try {
          await organizationsService.removeMember(orgId, username, authFetch);
          message.success(translate("organizations.memberRemovedSuccess"));
          loadMembers(members.page);
          // Removing yourself drops this org from your membership list.
          if (isSelf(username)) await refreshUserOrgs();
        } catch (error) {
          notifyFailure(error, "organizations.memberRemoveFailed");
        }
      },
    });
  };

  // ── Robot actions ─────────────────────────────────────────────────────────

  const handleAttachRobot = async (robotId) => {
    if (!robotId) {
      message.error(translate("organizations.pleaseEnterRobotId"));
      return;
    }
    setAttachingRobot(true);
    try {
      await organizationsService.addRobot(orgId, robotId, authFetch);
      message.success(translate("organizations.robotAddedSuccess"));
      setIsAttachRobotVisible(false);
      loadRobots(robots.page);
    } catch (error) {
      notifyFailure(error, "organizations.robotAddFailed");
    } finally {
      setAttachingRobot(false);
    }
  };

  const handleDetachRobot = (robotId) => {
    Modal.confirm({
      title: translate("organizations.confirmRemoveRobot"),
      content: `${translate("organizations.robotId")}: ${robotId}`,
      okText: translate("common.yes"),
      cancelText: translate("common.no"),
      onOk: async () => {
        try {
          await organizationsService.removeRobot(orgId, robotId, authFetch);
          message.success(translate("organizations.robotRemovedSuccess"));
          loadRobots(robots.page);
        } catch (error) {
          notifyFailure(error, "organizations.robotRemoveFailed");
        }
      },
    });
  };

  // ── Organization info actions ─────────────────────────────────────────────

  const handleOpenEditOrg = () => {
    const organization = selectedOrg?.organization ?? {};
    setEditingOrg({
      org_id: organization.org_id,
      name: organization.name ?? "",
      description: organization.description ?? "",
      logo_path: organization.logo_path ?? null,
      logo_compact_path: organization.logo_compact_path ?? null,
    });
    setIsEditOrgVisible(true);
  };

  const handleUpdateOrganization = async () => {
    if (!editingOrg.name) {
      message.error(translate("organizations.pleaseEnterOrgName"));
      return;
    }
    try {
      await organizationsService.update(
        editingOrg.org_id,
        { name: editingOrg.name, description: editingOrg.description },
        authFetch,
      );
      message.success(translate("organizations.organizationUpdatedSuccess"));
      setIsEditOrgVisible(false);
      // Refresh so this page's header and the sidebar switcher pick up the name.
      await refreshUserOrgs();
    } catch (error) {
      notifyFailure(error, "organizations.organizationUpdateFailed");
    }
  };

  const handleOrgLogoUpload = async (file, logoType) => {
    if (!editingOrg.org_id) return;
    const formData = new FormData();
    formData.append("file", file);
    const url =
      logoType === "logo"
        ? servicesUrlConfig.ORGS_API.upload_logo(editingOrg.org_id)
        : servicesUrlConfig.ORGS_API.upload_logo_compact(editingOrg.org_id);
    try {
      // Pass FormData straight through; axios sets the multipart boundary.
      const response = await authFetch(url, { method: "post", body: formData });
      const field = logoType === "logo" ? "logo_path" : "logo_compact_path";
      setEditingOrg((prev) => ({ ...prev, [field]: response.data[field] }));
      await refreshUserOrgs();
    } catch (error) {
      notifyFailure(error, "organizations.organizationUpdateFailed");
    }
  };

  const handleOrgLogoDelete = async (logoType) => {
    if (!editingOrg.org_id) return;
    const url =
      logoType === "logo"
        ? servicesUrlConfig.ORGS_API.delete_logo(editingOrg.org_id)
        : servicesUrlConfig.ORGS_API.delete_logo_compact(editingOrg.org_id);
    try {
      await authFetch(url, { method: "delete" });
      const field = logoType === "logo" ? "logo_path" : "logo_compact_path";
      setEditingOrg((prev) => ({ ...prev, [field]: null }));
      await refreshUserOrgs();
    } catch (error) {
      notifyFailure(error, "organizations.organizationUpdateFailed");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (orgsLoading && !selectedOrg) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!selectedOrg) {
    return (
      <div className="tabled" style={{ padding: 48 }}>
        <Empty
          description={
            <>
              <div>{translate("organizations.orgPage.noOrgTitle")}</div>
              <Typography.Text type="secondary">
                {translate("organizations.orgPage.noOrgHint")}
              </Typography.Text>
            </>
          }
        />
      </div>
    );
  }

  const membersColumns = createOrgMembersColumns(translate, {
    showActions: canManage,
    onEdit: handleEditMember,
    onDelete: handleRemoveMember,
  });

  const robotColumns = createOrgRobotColumns(translate, {
    showActions: canManage,
    onDelete: handleDetachRobot,
  });

  const tablePagination = (pageData, onChange) => ({
    current: pageData.page || PAGINATION_DEFAULTS.page,
    pageSize: pageData.per_page || PAGINATION_DEFAULTS.perPage,
    total: pageData.total || 0,
    showSizeChanger: true,
    pageSizeOptions: PAGINATION_DEFAULTS.pageSizeOptions,
    onChange,
  });

  const tabItems = [
    {
      key: "members",
      label: translate("organizations.orgPage.membersTab"),
      children: (
        <Card
          variant="borderless"
          className="criclebox tablespace"
          title={translate("organizations.organizationMembers")}
          extra={
            <Button
              icon={<PlusOutlined />}
              style={BUTTON_STYLES.add}
              onClick={() => setIsAddMemberVisible(true)}
            >
              {translate("organizations.addMember")}
            </Button>
          }
        >
          <div className="table-responsive">
            <Table
              columns={membersColumns}
              dataSource={members.results}
              rowKey={(record) => record.user.username}
              loading={membersLoading}
              pagination={tablePagination(members, loadMembers)}
              className="ant-border-space"
            />
          </div>
        </Card>
      ),
    },
    {
      key: "robots",
      label: translate("organizations.orgPage.robotsTab"),
      children: (
        <Card
          variant="borderless"
          className="criclebox tablespace"
          title={translate("organizations.organizationRobots")}
          extra={
            <Button
              icon={<PlusOutlined />}
              style={BUTTON_STYLES.add}
              onClick={() => setIsAttachRobotVisible(true)}
            >
              {translate("organizations.orgPage.attachRobot")}
            </Button>
          }
        >
          <div className="table-responsive">
            <Table
              columns={robotColumns}
              dataSource={robots.results}
              rowKey="robot_id"
              loading={robotsLoading}
              pagination={tablePagination(robots, loadRobots)}
              className="ant-border-space"
              onRow={(record) => ({
                onDoubleClick: () => setRobotDetail(record),
              })}
            />
          </div>
        </Card>
      ),
    },
    {
      key: "storage",
      label: translate("organizations.orgPage.storageTab"),
      children: (
        <Card variant="borderless" className="criclebox">
          <OrgStorageSection
            key={orgId}
            orgId={orgId}
            canManage={canManage}
            translate={translate}
          />
        </Card>
      ),
    },
  ];

  return (
    <>
      <div className="tabled">
        <OrgInfoHeader
          orgEntry={selectedOrg}
          canEdit={canManage}
          onEditClick={handleOpenEditOrg}
          translate={translate}
        />

        <OrgKpiRow
          memberTotal={members.total}
          robotTotal={robots.total}
          mcapStats={mcapStats}
          membersLoading={membersLoading}
          robotsLoading={robotsLoading}
          statsLoading={statsLoading}
          translate={translate}
        />

        {canManage ? (
          <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
        ) : (
          <Typography.Text type="secondary">
            {translate("organizations.orgPage.adminOnlyHint")}
          </Typography.Text>
        )}
      </div>

      <AddMemberModal
        visible={isAddMemberVisible}
        member={newMember}
        onOk={handleAddMember}
        onCancel={() => setIsAddMemberVisible(false)}
        onChange={(updates) => setNewMember({ ...newMember, ...updates })}
        translate={translate}
      />

      <EditMemberModal
        visible={isEditMemberVisible}
        member={editingMember}
        onOk={handleUpdateMemberRoles}
        onCancel={() => setIsEditMemberVisible(false)}
        onChange={(updates) => setEditingMember({ ...editingMember, ...updates })}
        translate={translate}
      />

      <AttachRobotModal
        visible={isAttachRobotVisible}
        onOk={handleAttachRobot}
        onCancel={() => setIsAttachRobotVisible(false)}
        existingRobotIds={robots.results.map((robot) => robot.robot_id)}
        confirmLoading={attachingRobot}
        translate={translate}
      />

      <RobotDetailModal
        visible={!!robotDetail}
        robot={robotDetail}
        canViewApiKey={canManage}
        onClose={() => setRobotDetail(null)}
        translate={translate}
      />

      <EditOrganizationModal
        visible={isEditOrgVisible}
        organization={editingOrg}
        onOk={handleUpdateOrganization}
        onCancel={() => setIsEditOrgVisible(false)}
        onChange={(updates) => setEditingOrg({ ...editingOrg, ...updates })}
        onLogoUpload={handleOrgLogoUpload}
        onLogoDelete={handleOrgLogoDelete}
        canManageLogo={canManage}
        showStorage={false}
        translate={translate}
      />
    </>
  );
};

export default OrganizationPage;
